const RESPONSE_SECTION_MARKER_REGEX = /\[RESPONSE_SECTION_\d+\]/g;
const isThinkingBlock = block => block.type === 'THINKING' || block.type === 'REDACTED_THINKING';
const isUserTextMessage = message => message.role === 'USER' && !message.parentMessageId && message.blocks.some(block => block.type === 'TEXT');
const isIntermediateTextMessage = message => message.id.endsWith('-text');
const hasVisibleContent = message => message.blocks.some(block => block.type !== 'TOOL' && !isThinkingBlock(block));
const hasResponseSectionMarker = message => message.blocks.some(block => block.type === 'TEXT' && !!block.text.match(RESPONSE_SECTION_MARKER_REGEX));

// Whether the message has real narrative text of its own, as opposed to
// only `[RESPONSE_SECTION_n]` markers (e.g. a message whose sole content is
// a CTA/suggestion button group). Used to decide whether a message is
// worth attaching feedback controls to.
export const hasNarrativeText = message => message.blocks.some(block => block.type === 'TEXT' && block.text.replace(RESPONSE_SECTION_MARKER_REGEX, '').trim().length > 0);

// Whether a later message stands on its own as the turn's real content,
// so the earlier `{id}-text` intermediate can be dropped. Substantial
// narrative text always counts; a message with no narrative text but with
// an embedded `[RESPONSE_SECTION_n]` marker (e.g. CTA/suggestion buttons
// attached to the final message of the turn) also counts, otherwise both
// the intermediate and the final message stay visible and each renders its
// own feedback controls for the same turn.
const canSupersedeIntermediateText = message => {
  if (message.phase === 'commentary' || message.role !== 'ASSISTANT' || isIntermediateTextMessage(message)) {
    return false;
  }
  return hasNarrativeText(message) || hasResponseSectionMarker(message);
};
const isSupersededIntermediateText = (messages, index) => {
  for (let i = index + 1; i < messages.length; i += 1) {
    const later = messages[i];
    if (isUserTextMessage(later)) {
      return false;
    }
    if (canSupersedeIntermediateText(later)) {
      return true;
    }
  }
  return false;
};
const stripThinkingBlocks = message => Object.assign({}, message, {
  blocks: message.blocks.filter(block => !isThinkingBlock(block))
});
export const getVisibleChatMessages = messages => messages.filter((message, index) => {
  if (message.phase === 'commentary') {
    return false;
  }
  if (!hasVisibleContent(message)) {
    return false;
  }

  // Breeze Engine sometimes returns assistant responses as USER blocks.
  if (message.role === 'USER' && message.parentMessageId) {
    return false;
  }

  // When the LLM emits text before a tool call, breeze-engine-lib splits
  // that turn into `{id}-text` + `{id}-tool`. Hide the intermediate text
  // only once a later final assistant text message exists in the same turn;
  // otherwise it is the sole text source and must stay visible.
  if (isIntermediateTextMessage(message) && isSupersededIntermediateText(messages, index)) {
    return false;
  }
  return true;
}).map(stripThinkingBlocks);